import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react"

import { useStores } from "stores/root-store"

import AlertDialog from "components/common/alert-dialog"
import { LoggingHelper } from "utils/logging-helper"
import { FormWrapper } from "components/common-styles"

import SaleOrderDelivery from "./sale-order-delivery"
import SaleOrderProduct from "./sale-order-product"

import { errorType, trackEvent } from "utils/analytics"
import CommonFormActions from "components/common/common-form-actions"
import { ArrowBack } from "@mui/icons-material"

// Free-text header fields a user might empty on edit, with their form labels.
const CLEARABLE_FIELDS = {
  ship_to_name: "Delivery Name",
  shipping_method: "Shipping Method",
  ship_to: "Company Name",
  delivery_code: "Delivery Code",
  brand_code: "Brand Code",
  ship_to_address1: "Delivery Address Line 1",
  special_details_3: "Delivery Attention to",
  ship_to_address2: "Delivery Address Line 2",
  ship_to_code: "Ship to Code",
  ship_to_city: "Delivery Suburb",
  special_details_2: "Ship to Fax Num",
  ship_to_phone_num: "Delivery Phone Number",
  ship_to_postal_code: "Delivery Postal Code",
  ship_to_email_address: "Delivery Email Address",
  delivery_instructions: "Delivery Instructions",
  customer_name: "Customer Name",
  customer: "Customer Company Name",
  customer_address1: "Customer Address Line 1",
  customer_phone_num: "Customer Phone Number",
  customer_address2: "Customer Address Line 2",
  customer_email_address: "Customer Email Address",
  customer_city: "Customer Suburb",
  customer_code: "Customer Code",
}

const SaleOrderForm = ({ onClose, onSave }) => {
  const { SaleOrderStore } = useStores()
  const initialValues = useMemo(() => {
    return {
      ship_to_name: "",
      shipping_method: "",
      ship_to: "",
      erp_order: "",
      ship_to_country: "",
      warehouse: "",
      carrier: "",
      carrier_service: "",
      ship_to_address1: "",
      ship_to_code: "",
      ship_to_address2: "",
      delivery_code: "",
      brand_code: "",
      ship_to_city: "",
      incoterm: "",
      sales_channel: "",
      ship_to_state: "",
      ship_to_postal_code: "",
      ship_to_email_address: "",
      ship_to_phone_num: "",
      special_details_2: "",
      special_details_3: "",
      customer_name: "",
      customer_country: "",
      customer: "",
      customer_address1: "",
      customer_phone_num: "",
      customer_address2: "",
      customer_email_address: "",
      customer_city: "",
      customer_code: "",
      customer_state: "",
      customer_postal_code: "",
    }
  }, [])

  const [openAlert, setOpenAlert] = useState(false)
  const [alertContent, setAlertContent] = useState("")

  const formRef = useRef()
  const productsRef = useRef()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [saleOrderInfo, setSaleOrderInfo] = useState(initialValues)
  const [products, setProducts] = useState([])
  const [wasSuccess, setWasSuccess] = useState(true)
  const [erpOrder, setErpOrder] = useState("")
  const [deliveryCountry, setDeliveryCountry] = useState("")

  const initData = useCallback(() => {
    if (SaleOrderStore.deliveryInfo) {
      const data = { ...SaleOrderStore.deliveryInfo }
      setProducts(JSON.parse(JSON.stringify(data.details || [])))
      setSaleOrderInfo(data)
      setErpOrder(data.erp_order)
      setDeliveryCountry(data.ship_to_country)
    } else {
      setSaleOrderInfo(initialValues)
    }
  }, [SaleOrderStore.deliveryInfo, initialValues])

  useEffect(() => {
    initData()
  }, [initData])

  const handleSubmit = async () => {
    const resInfo = await formRef.current.getFormDataIfValid()
    if (!resInfo) return

    // Scale ignores an empty field on update, so blanking one would report
    // success while leaving the old value. Make the user enter "." instead.
    if (SaleOrderStore.deliveryInfo) {
      const blanked = Object.keys(CLEARABLE_FIELDS).filter(key => String(SaleOrderStore.deliveryInfo[key] ?? "").trim() && !String(resInfo[key] ?? "").trim())
      if (blanked.length) {
        setWasSuccess(false)
        setAlertContent(`To clear ${blanked.map(key => CLEARABLE_FIELDS[key]).join(", ")}, enter a full stop (.) instead of leaving it empty.`)
        setOpenAlert(true)
        return
      }
    }

    const resProducts = await productsRef.current.getProductsIfValid()
    if (!resProducts) return

    const newInfo = { ...saleOrderInfo, ...resInfo, products: resProducts }

    setIsSubmitting(true)
    setOpenAlert(true)
    const result = await SaleOrderStore.addOrUpdateSaleOrder(newInfo)
    LoggingHelper.writeLog("SaleOrderForm", "submit", `${result ? JSON.stringify(result) : ""}`)
    setIsSubmitting(false)

    setWasSuccess(true)
    let msg = SaleOrderStore.deliveryInfo ? "Data Updated Successfully" : "New Data Added Successfully"
    const acceptedTransactions = result?.acceptedTransactions || result?.resource?.acceptedTransactions || result?.error?.context?.acceptedTransactions || 0
    if (result && (result.resource?.message || result.error?.message || result.message || typeof result == "string") && acceptedTransactions < 1) {
      setWasSuccess(false)
      msg = result.resource?.message || result.error?.message || result.message || result
      trackEvent("record_save", { entity: "sales_order", mode: SaleOrderStore.deliveryInfo ? "edit" : "add", result: "failed", error_type: errorType(result) })
    } else {
      trackEvent("record_save", { entity: "sales_order", mode: SaleOrderStore.deliveryInfo ? "edit" : "add", result: "success" })
    }
    setAlertContent(msg)
  }

  const onOK = () => {
    setOpenAlert(false)
    if (wasSuccess) {
      onSave()
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <>
      <FormWrapper spacing={2}>
        <Stack direction={"row"} spacing={2}>
          <Tooltip placement="top" title="Back">
            <IconButton onClick={handleClose}>
              <ArrowBack />
            </IconButton>
          </Tooltip>

          <Box>
            {SaleOrderStore.deliveryInfo ? (
              <>
                <Typography variant="h5">Edit Sales Order: {SaleOrderStore.deliveryInfo.erp_order}</Typography>
                <small>Review delivery and product details on an existing sales order</small>
              </>
            ) : (
              <>
                <Typography variant="h5">New Sales Order</Typography>
                <small>Enter delivery and product details to create a new sales order</small>
              </>
            )}
          </Box>
        </Stack>

        <SaleOrderDelivery saleOrderInfo={saleOrderInfo} ref={formRef} onErpOrderChange={setErpOrder} onDeliveryCountryChange={setDeliveryCountry} />
        <SaleOrderProduct erp_order={erpOrder} deliveryCountry={deliveryCountry} curProducts={products} isSubmitting={isSubmitting} ref={productsRef} />

        <CommonFormActions isSubmitting={isSubmitting} handleSubmit={handleSubmit} handleClose={handleClose} />
      </FormWrapper>
      <AlertDialog isProgress={isSubmitting} content={alertContent} open={openAlert} onClose={() => setOpenAlert(false)} onOK={onOK} />
    </>
  )
}

export default memo(SaleOrderForm)
